import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : 'http://localhost:3000/api';

const SalaryFundHistoryModal = ({ isOpen, onClose, onRefresh }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token');

    useEffect(() => {
        if (isOpen) {
            fetchHistory();
        }
    }, [isOpen]);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/finance/salary-funds`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHistory(response.data);
        } catch (error) {
            console.error('Error fetching salary fund history:', error);
            Swal.fire('Error', 'Gagal memuat riwayat proporsi gaji', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatInputCurrency = (value) => {
        const numericValue = value.toString().replace(/[^0-9]/g, '');
        if (!numericValue) return '';
        return parseInt(numericValue, 10).toLocaleString('id-ID');
    };

    const unformatCurrency = (value) => {
        return value.toString().replace(/[^0-9]/g, '');
    };

    const handleEdit = async (record) => {
        const { value: formValues } = await Swal.fire({
            title: 'Edit Proporsi Gaji',
            html: `
        <div class="text-left">
          <label class="block text-sm font-medium mb-1">Jumlah (IDR) *</label>
          <input id="swal-input1" class="swal2-input w-full max-w-full m-0 mb-4 px-3 py-2 box-border" placeholder="0" value="${formatInputCurrency(record.amount)}">
          
          <label class="block text-sm font-medium mb-1">Keterangan</label>
          <input id="swal-input2" class="swal2-input w-full max-w-full m-0 px-3 py-2 box-border" placeholder="Deskripsi" value="${record.description || ''}">
        </div>
      `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Simpan Perubahan',
            cancelButtonText: 'Batal',
            didOpen: () => {
                const input1 = document.getElementById('swal-input1');
                input1.addEventListener('input', (e) => {
                    e.target.value = formatInputCurrency(e.target.value);
                });
            },
            preConfirm: () => {
                const rawAmount = document.getElementById('swal-input1').value;
                const parsedAmount = parseFloat(unformatCurrency(rawAmount));
                const description = document.getElementById('swal-input2').value;

                if (isNaN(parsedAmount) || parsedAmount <= 0) {
                    Swal.showValidationMessage('Masukan nominal yang valid');
                    return false;
                }

                return {
                    amount: parsedAmount,
                    description: description
                };
            }
        });

        if (formValues) {
            try {
                await axios.put(`${API_URL}/finance/salary-funds/${record.id}`, formValues, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Berhasil', 'Data proporsi gaji berhasil diperbarui', 'success');
                fetchHistory();
                if (onRefresh) onRefresh();
            } catch (error) {
                console.error('Error updating record:', error);
                Swal.fire('Error', 'Gagal memperbarui proporsi gaji', 'error');
            }
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Yakin hapus proporsi ini?',
            text: "Jika dihapus, total sisa budget gaji akan ikut berkurang!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Ya, hapus!',
            cancelButtonText: 'Batal'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${API_URL}/finance/salary-funds/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Berhasil', 'Proporsi gaji berhasil dihapus', 'success');
                fetchHistory();
                if (onRefresh) onRefresh();
            } catch (error) {
                console.error('Error deleting record:', error);
                Swal.fire('Error', 'Gagal menghapus proporsi gaji', 'error');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
                <div className="flex justify-between items-center mb-6 pb-4 border-b">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Riwayat Proporsi Gaji</h2>
                        <p className="text-sm text-gray-500 mt-1">Daftar penambahan dana ke dalam kas gaji terpusat</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 bg-gray-100 p-2 rounded-full transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-[300px]">
                    {loading ? (
                        <div className="flex justify-center items-center h-full text-gray-500">
                            <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Memuat data...
                        </div>
                    ) : history.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-lg font-medium text-gray-500">Belum ada riwayat proporsi gaji.</p>
                            <p className="text-sm mt-1">Tambahkan proporsi terlebih dahulu dari dashboard finance.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tanggal</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nominal</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Pembuat</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Keterangan</th>
                                        <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {history.map((record) => (
                                        <tr key={record.id} className="hover:bg-amber-50/30 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                {formatDate(record.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-amber-600">
                                                {formatCurrency(record.amount)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {record.creator_name || 'System'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={record.description}>
                                                {record.description || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(record)}
                                                        className="bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 px-3 py-1.5 rounded text-xs font-semibold border border-blue-200 transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(record.id)}
                                                        className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 px-3 py-1.5 rounded text-xs font-semibold border border-red-200 transition-colors"
                                                    >
                                                        Hapus
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-4 border-t text-right">
                    <button
                        onClick={onClose}
                        className="bg-gray-100 border border-gray-300 text-gray-700 font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-200 transition-colors focus:ring-2 focus:ring-gray-400"
                    >
                        Tutup Riwayat
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SalaryFundHistoryModal;
